import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF } from '@react-three/drei';
import * as THREE from 'three';

// Constants
const ZOOM_FACTOR = 4;

function Model({
  url,
  guessesLeft,
  autoRotate,
  onManualCameraMove,
  resetTrigger,
  controlsRef,
  correctGuess,
  onInitialCameraComputed,
  isFirstRound
}) {
  const group = useRef();
  const { scene, error } = useGLTF(url);
  const [rotation, setRotation] = useState([0, 0, 0]);
  const { camera } = useThree();

  const initialCameraZ = useRef();

  useEffect(() => {
    if (error) {
      console.error("Error loading model:", error);
      return;
    }
  }, [error]);

  const calculateCameraPosition = useCallback(() => {
    const box = new THREE.Box3().setFromObject(scene);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = camera.fov * (Math.PI / 180);
    let cameraZ = Math.abs(maxDim / 2 * Math.tan(fov / 2)) * ZOOM_FACTOR;
    return cameraZ;
  }, [scene, camera]);


  useEffect(() => {
    if (!scene) return;
    const cameraZ = calculateCameraPosition();
    initialCameraZ.current = cameraZ;
    camera.position.set(0, 0, cameraZ);
    camera.lookAt(0, 0, 0);

    if (typeof onInitialCameraComputed === 'function') {
      onInitialCameraComputed([0, 0, cameraZ]);
    }
  }, [scene, camera, onInitialCameraComputed, calculateCameraPosition]);


  useEffect(() => {
    if (!scene) return;

    const box = new THREE.Box3().setFromObject(scene);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = camera.fov * (Math.PI / 180);
    let cameraZ = Math.abs(maxDim / 2 * Math.tan(fov / 2)) * ZOOM_FACTOR;
    camera.position.z = cameraZ;

    scene.position.x = -center.x;
    scene.position.y = -center.y;
    scene.position.z = -center.z;

    const clonedScene = scene.clone();
    group.current.add(clonedScene);

    clonedScene.traverse((child) => {
      if (child.isMesh) {
        child.userData.originalMaterial = child.material;
      }
    });

    scene.position.set(0, 0, 0);

    const currentGroup = group.current;
    return () => {
      if (currentGroup) {
        currentGroup.remove(clonedScene);
      }
    };
  }, [url, scene, camera]);

  useFrame(() => {
    if (autoRotate) {
      setRotation(([x, y, z]) => [x, y + 0.001, z]);
    }
    if (group.current) {
      group.current.rotation.set(...rotation);
    }
  });

  useEffect(() => {
    if (!group.current) return;

    group.current.traverse((child) => {
      if (child.isMesh) {
        if (correctGuess) {
          child.material = child.userData.originalMaterial;
        } else if (isFirstRound) {
          child.material = new THREE.MeshBasicMaterial({ color: 'white' });
        } else if (guessesLeft === 6) {
          child.material = new THREE.MeshBasicMaterial({ color: 'white' });
        } else if (guessesLeft === 5) {
          child.material = new THREE.MeshStandardMaterial({ color: 'white', metalness: 1, roughness: 1 });
        } else if (guessesLeft <= 4) {
          child.material = child.userData.originalMaterial;
        }
      }
    });
  }, [guessesLeft, correctGuess, isFirstRound]);

  return <group ref={group} />;
}

export default function ModelViewer({
  url,
  guessesLeft,
  enableControls,
  autoRotate,
  onManualCameraMove,
  correctGuess,
  isFirstRound
}) {
  const [error, setError] = useState(null);
  const [initialCameraPos, setInitialCameraPos] = useState([0, 0, 100]); // Will be updated dynamically
  const controlsRef = useRef();
  const cameraRef = useRef();

  useEffect(() => {
    if (!url) return;
    const ext = url.split('.').pop().toLowerCase();
    if (ext !== 'glb') {
      setError(`Only GLB format is supported. Found: .${ext}`);
    } else {
      setError(null);
    }
  }, [url]);


  if (error) return <p>Error: {error}</p>;

  if (!url) {
    return (
      <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <Canvas
        camera={{ position: initialCameraPos, fov: 60 }}
        style={{ background: 'black', height: '100%', width: '100%' }}
        onCreated={({ camera }) => {
          cameraRef.current = camera;
        }}
      >
        <ambientLight intensity={3} />
        <directionalLight position={[10, 10, 10]} intensity={2} />
        <directionalLight position={[-10, -10, -10]} intensity={2} />
        {enableControls && (
          <OrbitControls
            ref={controlsRef}
            onStart={onManualCameraMove}
          />
        )}
        <Model
          url={url}
          guessesLeft={guessesLeft}
          autoRotate={autoRotate}
          onManualCameraMove={onManualCameraMove}
          controlsRef={controlsRef}
          correctGuess={correctGuess}
          onInitialCameraComputed={setInitialCameraPos}
          isFirstRound={isFirstRound}
        />
      </Canvas>

    </div>
  );
}
